import type { ReactNode } from 'react';
import { TotpAuthGate } from '@lemontaigu/platform-ui';
import { CAPABILITIES, can, actorOf } from '@lemontaigu/platform';
import { createSupabaseCapabilityStore } from '@lemontaigu/platform/adapters/client';
import { supabase } from '../supabase.js';

/**
 * Back-office gate (APPS-REORG R1): authenticated → TOTP (AAL2) → **operator**
 * (`platform.admin`). Same password + mandatory-TOTP flow as the desk, but the
 * membership check requires the admin capability, so only bos@ passes: the
 * shared hotel@ desk login holds `platform.staff` but not `platform.admin` and
 * is rejected here.
 *
 * FIXED 2026-07-27 (found during ladder review R4): this called
 * `supabase.rpc('is_admin')` directly, which migration 0064 (step 9,
 * increment 4d) DROPPED from the database the same day — every admin login
 * had been failing closed since. `is_admin`/`is_staff` never had a reason to
 * survive as RPCs once `has_capability()` existed; the miss was that this
 * gate runs BEFORE `CapabilityProvider` mounts (it wraps the app, not the
 * other way round), so it was never swept when every other `is_admin` probe
 * was replaced at step 8. Uses the sanctioned adapter directly rather than
 * inlining a raw `.rpc('my_capabilities')` call — `capabilityStore.ts`'s own
 * header states it is "the ONLY file in the capability layer permitted to
 * name the RPC," and this gate has no reason to be the second one.
 *
 * Flow shared via @lemontaigu/platform-ui (ladder step 4, F8); this file is the
 * composition root binding the concrete client to it.
 */
async function userIsAdmin(): Promise<boolean> {
  try {
    const caps = await createSupabaseCapabilityStore(supabase).mine();
    return can(actorOf(caps), CAPABILITIES.PLATFORM_ADMIN);
  } catch {
    return false;
  }
}

export function AuthGate({ children }: { children: ReactNode }) {
  return (
    <TotpAuthGate
      client={supabase}
      brand="Administration"
      checkMembership={userIsAdmin}
      deniedMessage="Ce compte n’a pas accès à l’administration. Cet espace est réservé à la direction."
    >
      {children}
    </TotpAuthGate>
  );
}
